import {
  Box,
  Grid2,
  Typography,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  TextField,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DiscountIcon from "@mui/icons-material/Discount";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import { useEffect, useState, useRef } from "react";
import { useAtom } from "jotai";
import {
  invoiceFilterStateAtom,
  selectedOrgIdAtom,
  selectedAccountIdAtom,
  userAtom,
} from "../../atoms";
import {
  DropdownItem,
  OrganizationInfo,
  Address,
  AccountInfo,
  CreateInvoiceDatagridRef,
  InvoiceAdjustmentPayload,
  OneOffInvoiceItem,
} from "../../types";
import { AdjustmentType, Role } from "../../enums";
import {
  useFetchAccountsQuery,
  useFetchOrganizationsQuery,
  useFetchAccountInfoQuery,
  useFetchOrganizationInfoQuery,
} from "../../api";
import { useCreateOneOffInvoiceMutation } from "../../api/invoiceService";
import { CreateRegenerateInvoiceModal } from "../common/modal/CreateRegenerateInvoiceModal";
import { AccountModel, OrganizationModel } from "../../models";
import { formatCurrency, theme } from "../../utils";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CreateAccountDrawer } from "./fragments";
import { CreateInvoiceDatagrid } from "./fragments";
import { Button } from "../common/button";
import { Modal } from "../common";
import { AdjustmentsForm } from "../invoice/fragments";
import { useUpdateAccountFinancialMutation } from "../../api/accountService";
import {
  ROUTE_ADM_INVOICES,
  ROUTE_SADM_INVOICES,
} from "../../routes/constants";

export const CreateOneOffInvoice = () => {
  const [user] = useAtom(userAtom);
  const [filterValues, setFilterValues] = useAtom(invoiceFilterStateAtom);
  const [selectedOrgId, setSelectedOrgId] = useAtom(selectedOrgIdAtom);
  const [selectedAccountId, setSelectedAccountId] = useAtom(
    selectedAccountIdAtom,
  );
  const [queryParams, setQueryParams] = useSearchParams();
  const navigate = useNavigate();
  const [totalInvoiceAmount, setTotalInvoiceAmount] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [feeAmount, setFeeAmount] = useState(0);
  const [discountPercentTotal, setDiscountPercentTotal] = useState(0);
  const [purchaseOrder, setPurchaseOrder] = useState<string>("");

  const percentageDiscountValue =
    (totalInvoiceAmount * discountPercentTotal) / 100;
  const finalAmount =
    totalInvoiceAmount - percentageDiscountValue - discountAmount + feeAmount;
  const totalDiscount = discountAmount;
  const hasBoth = discountPercentTotal > 0 && discountAmount > 0;
  const [organizationsDropdown, setOrganizationsDropdown] = useState<
    DropdownItem[]
  >([]);
  const [accountsDropdown, setAccountsDropdown] = useState<DropdownItem[]>([
    { key: "+ Add New", value: "ADD_NEW_ACCOUNT" },
  ]);
  const [requestId, setRequestId] = useState<string>("");
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [orgIdForModal, setOrgIdForModal] = useState(0);
  const [accountIdForModal, setAccountIdForModal] = useState(0);
  const [openCreateAccountDrawer, setOpenCreateAccountDrawer] = useState(false);
  const handleAccountCreated = async (accountId: number) => {
    await fetchAccountsQuery.refetch();

    setSelectedAccountId(accountId);

    setFilterValues((prev) => ({
      ...prev,
      accountId,
    }));
  };

  const [orgInfo, setOrgInfo] = useState<OrganizationInfo>();
  const [accountInfo, setAccountInfo] = useState<AccountInfo>();

  const fetchOrganizationsQuery = useFetchOrganizationsQuery({
    enabled: user?.roleCode === Role.SuperAdmin,
  });

  const orgId = filterValues.orgId || selectedOrgId || user?.orgId || 0;

  const fetchAccountsQuery = useFetchAccountsQuery(orgId, {
    enabled: orgId > 0,
  });

  const createOneOffInvoiceMutation = useCreateOneOffInvoiceMutation();
  const updateAccountFinancialMutation = useUpdateAccountFinancialMutation();

  const accountInfoParams = {
    orgId: selectedOrgId,
    accountId: selectedAccountId || 0,
  };

  const isAllowedToFetchAccountInfo = Boolean(
    accountInfoParams.orgId && accountInfoParams.accountId,
  );

  const accountInfoQuery = useFetchAccountInfoQuery(
    accountInfoParams.orgId,
    accountInfoParams.accountId,
    {
      contacts: true,
      locations: false,
      financial: true,
      fleets: false,
      charges: false,
    },
    {
      enabled: isAllowedToFetchAccountInfo,
    },
  );
  const hasAddress = Boolean(
    accountInfo?.baseInfo.accountAddress?.addressLine1 ||
    accountInfo?.baseInfo.accountAddress?.city ||
    accountInfo?.baseInfo.accountAddress?.state ||
    accountInfo?.baseInfo.accountAddress?.zip,
  );

  const organizationInfoQuery = useFetchOrganizationInfoQuery(selectedOrgId, {
    enabled: selectedOrgId ? true : false,
  });

  const isOrgInfoLoading = !selectedOrgId || organizationInfoQuery.isLoading;
  const isAccountInfoLoading =
    (selectedAccountId ?? 0) > 0 && accountInfoQuery.isLoading;

  const LineSkeleton = ({ width = 200 }) => (
    <Skeleton variant="text" width={width} height={20} />
  );
  const datagridRef = useRef<CreateInvoiceDatagridRef>(null);
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const handleAdjustmentEdit = (data: InvoiceAdjustmentPayload) => {
    if (data.discountAmount) {
      const isPercentage = data.discountType === "percentage";

      if (isPercentage) {
        const percent = Number(data.discountValue);

        setDiscountPercentTotal((prev) => prev + percent);

        const amount = (totalInvoiceAmount * percent) / 100;

        datagridRef.current?.addAdjustmentRow({
          label: "Discount",
          amount: amount,
          note: data.discountNote,
          isPercentage: true,
          displayValue: percent,
        });
      } else {
        const value = Number(data.discountAmount);

        datagridRef.current?.addAdjustmentRow({
          label: "Discount",
          amount: value,
          note: data.discountNote,
        });

        setDiscountAmount((prev) => prev + value);
      }
    }

    if (data.feeAmount || data.feeType === "percentage") {
      if (data.feeType === "percentage") {
        const percent = Number(data.feeValue);

        const amount = (totalInvoiceAmount * percent) / 100;

        setFeeAmount((prev) => prev + amount);

        datagridRef.current?.addAdjustmentRow({
          label: "Fee",
          amount: amount,
          note: data.feeNote,
          isPercentage: true,
          displayValue: percent,
        });
      } else {
        const value = Number(data.feeAmount);

        setFeeAmount((prev) => prev + value);

        datagridRef.current?.addAdjustmentRow({
          label: "Fee",
          amount: value,
          note: data.feeNote,
          isPercentage: false,
        });
      }
    }

    setIsAdjustmentModalOpen(false);
  };

  const ContactBlockSkeleton = () => (
    <>
      <LineSkeleton width={180} />
      <LineSkeleton width={220} />
      <LineSkeleton width={200} />
    </>
  );

  const formatAddress = (addr?: Address) => {
    if (!addr) return "";

    const lines = [
      addr.addressLine1,
      addr.addressLine2,
      [addr.city, addr.state, addr.zip].filter(Boolean).join(", "),
    ].filter(Boolean);

    return lines.join("\n");
  };

  useEffect(() => {
    if (fetchOrganizationsQuery.isSuccess) {
      const dropdown = OrganizationModel.buildOrganizationDropdown(
        fetchOrganizationsQuery.data,
      );

      setOrganizationsDropdown(dropdown);

      if (dropdown.length > 0 && !selectedOrgId) {
        const orgIdFromQuery = Number(queryParams.get("orgId"));
        const firstOrgId = orgIdFromQuery || Number(dropdown[0].value);

        setSelectedOrgId(firstOrgId);
        setFilterValues({ orgId: firstOrgId });
      }
    }
  }, [fetchOrganizationsQuery.isSuccess]);

  useEffect(() => {
    if (!fetchAccountsQuery.data) return;

    const dropdown = AccountModel.buildAccountsDropdown(
      fetchAccountsQuery.data,
    );

    const dropdownWithAddNew = [
      { key: "+ Add New", value: "ADD_NEW_ACCOUNT" },
      ...dropdown,
    ];

    setAccountsDropdown(dropdownWithAddNew);

    if (!selectedAccountId) {
      const firstAccount = dropdownWithAddNew.find(
        (item) => item.value !== "ADD_NEW_ACCOUNT",
      );

      if (firstAccount) {
        const firstAccountId = Number(firstAccount.value);

        setSelectedAccountId(firstAccountId);

        setFilterValues((prev) => ({
          ...prev,
          accountId: firstAccountId,
        }));
      }
    }
  }, [fetchAccountsQuery.data]);

  useEffect(() => {
    if (organizationInfoQuery.data) {
      setOrgInfo(organizationInfoQuery.data);
    }
  }, [organizationInfoQuery.data]);

  useEffect(() => {
    if (accountInfoQuery.data) {
      const data = accountInfoQuery.data;
      setAccountInfo(data);
      const purchaseOrder =
        data?.financialInfo?.oneOffPurchaseOrder ||
        data?.financialInfo?.purchaseOrder ||
        "";
      setPurchaseOrder(purchaseOrder);
    }
  }, [accountInfoQuery.data]);

  useEffect(() => {
    const filters = Object.entries({
      orgId: selectedOrgId,
      accountId: selectedAccountId,
    }).reduce((acc: any, [key, value]) => {
      if (value) acc[key] = value;
      return acc;
    }, {});

    const tab = queryParams.get("tab");

    setQueryParams({
      ...(tab && { tab }),
      ...filters,
    });
  }, [selectedOrgId, selectedAccountId]);

  useEffect(() => {
    const orgIdFromQuery = Number(queryParams.get("orgId")) || undefined;
    const accountIdFromQuery =
      Number(queryParams.get("accountId")) || undefined;

    if (orgIdFromQuery) {
      setSelectedOrgId(orgIdFromQuery);

      setFilterValues((prev) => ({
        ...prev,
        orgId: orgIdFromQuery,
      }));
    }

    if (accountIdFromQuery) {
      setSelectedAccountId(accountIdFromQuery);

      setFilterValues((prev) => ({
        ...prev,
        accountId: accountIdFromQuery,
      }));
    }
  }, [queryParams]);
  const handleCreateInvoice = () => {
    const isValid = datagridRef.current?.validate();
    if (!isValid) return;

    const rows = datagridRef.current?.getRows() as OneOffInvoiceItem[];

    const lineItems = rows
      ?.filter((r: OneOffInvoiceItem) => !r.isAdjustment)
      .map((item: OneOffInvoiceItem) => ({
        date:
          typeof item.date === "string" ? item.date : item.date?.toISOString(),
        service: item.service,
        quantity: Number(item.quantity),
        rate: Number(item.unitPrice),
      }));

    const adjustments = rows
      ?.filter(
        (
          r: OneOffInvoiceItem,
        ): r is OneOffInvoiceItem & {
          adjustmentType: AdjustmentType;
        } => r.isAdjustment && !!r.adjustmentType,
      )
      .map((item) => ({
        adjustmentType: item.adjustmentType,
        service: item.service,
        rate: Math.abs(Number(item.total || 0)),
      }));

    updateAccountFinancialMutation.mutate(
      {
        orgId: selectedOrgId,
        accountId: selectedAccountId || 0,
        financialInfo: {
          oneOffPurchaseOrder: purchaseOrder,
        },
      },
      {
        onSuccess: () => {
          createOneOffInvoiceMutation.mutate(
            {
              orgId: selectedOrgId,
              accountId: selectedAccountId || 0,
              lineItems,
              adjustments,
            },
            {
              onSuccess: (res) => {
                const requestId = res.data;

                setRequestId(requestId);
                setOrgIdForModal(selectedOrgId || 0);
                setAccountIdForModal(selectedAccountId || 0);
                setIsStatusModalOpen(true);
              },
            },
          );
        },
      },
    );
  };
  return (
    <Box
      sx={{
        marginTop: "50px",
        width: "100%",
        minHeight: "75vh",
        boxShadow: "0px 0px 4px rgba(0,0,0,0.25)",
        borderRadius: "10px",
        backgroundColor: theme.palette.secondary.light,
        p: 3,
      }}
    >
      <Grid2 container spacing={{ xs: 4, md: 10 }} sx={{ mb: 3 }}>
        <Grid2 size={{ xs: 12, md: 4 }} sx={{ pr: { md: 3 } }}>
          <Grid2 container direction="column" spacing={2.5}>
            {user?.roleCode === Role.SuperAdmin && (
              <Grid2>
                <FormControl fullWidth size="small">
                  <InputLabel>Organization</InputLabel>

                  <Select
                    value={filterValues.orgId || ""}
                    label="Organization"
                    onChange={(e) => {
                      const orgId = Number(e.target.value);

                      setAccountsDropdown([
                        { key: "+ Add New", value: "ADD_NEW_ACCOUNT" },
                      ]);
                      setAccountInfo(undefined);

                      setFilterValues((prev) => ({
                        ...prev,
                        orgId,
                        accountId: 0,
                      }));

                      setSelectedOrgId(orgId);
                      setSelectedAccountId(0);
                    }}
                  >
                    {organizationsDropdown.map((org, index) => (
                      <MenuItem key={index} value={org.value}>
                        {org.key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
            )}

            <Grid2>
              <FormControl fullWidth size="small">
                <InputLabel>Account</InputLabel>

                <Select
                  value={filterValues.accountId || ""}
                  label="Account"
                  onChange={(e) => {
                    const value = e.target.value;

                    if (String(value) === "ADD_NEW_ACCOUNT") {
                      setOpenCreateAccountDrawer(true);
                      return;
                    }

                    const accountId = Number(value);

                    setFilterValues((prev) => ({
                      ...prev,
                      accountId,
                    }));

                    setSelectedAccountId(accountId);
                  }}
                >
                  {accountsDropdown.map((account, index) => (
                    <MenuItem key={index} value={account.value}>
                      {account.key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>
            <Grid2>
              <TextField
                fullWidth
                size="small"
                label="Purchase Order"
                value={purchaseOrder}
                onChange={(e) => setPurchaseOrder(e.target.value)}
              />
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 8 }}>
          <Grid2
            container
            columnSpacing={{ xs: 2, md: 20 }}
            rowSpacing={2}
            justifyContent="flex-start"
          >
            <Grid2 size={{ xs: 12, md: 6 }} sx={{ pr: { md: 2 } }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                From
              </Typography>

              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                <LocationOnIcon
                  sx={{
                    fontSize: 18,
                    mr: 1,
                    mt: "3px",
                    color: "text.secondary",
                  }}
                />

                <Box>
                  {isOrgInfoLoading ? (
                    <ContactBlockSkeleton />
                  ) : (
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {orgInfo?.name}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PhoneIcon
                  sx={{ fontSize: 18, mr: 1, color: "text.secondary" }}
                />

                {isOrgInfoLoading ? (
                  <LineSkeleton />
                ) : (
                  <Typography sx={{ fontSize: 14 }}>
                    {orgInfo?.contactNumber}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PersonIcon
                  sx={{ fontSize: 18, mr: 1, color: "text.secondary" }}
                />

                {isOrgInfoLoading ? (
                  <LineSkeleton width={180} />
                ) : (
                  <Typography sx={{ fontSize: 14 }}>
                    {orgInfo?.contactPerson}
                  </Typography>
                )}
              </Box>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }} sx={{}}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                Bill To
              </Typography>

              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                <LocationOnIcon
                  sx={{
                    fontSize: 18,
                    mr: 1,
                    mt: "3px",
                    color: "text.secondary",
                  }}
                />

                <Box>
                  {isAccountInfoLoading ? (
                    <ContactBlockSkeleton />
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {accountInfo?.baseInfo.accountName}
                      </Typography>

                      {hasAddress && (
                        <Typography
                          sx={{ fontSize: 14, whiteSpace: "pre-line" }}
                        >
                          {formatAddress(accountInfo?.baseInfo.accountAddress)}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PhoneIcon
                  sx={{ fontSize: 18, mr: 1, color: "text.secondary" }}
                />

                {isAccountInfoLoading ? (
                  <LineSkeleton />
                ) : (
                  <Typography sx={{ fontSize: 14 }}>
                    {accountInfo?.baseInfo.contactNumber}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PersonIcon
                  sx={{ fontSize: 18, mr: 1, color: "text.secondary" }}
                />

                {isAccountInfoLoading ? (
                  <LineSkeleton width={180} />
                ) : (
                  <Typography sx={{ fontSize: 14 }}>
                    {accountInfo?.baseInfo.contactPerson}
                  </Typography>
                )}
              </Box>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>

      <Grid2
        container
        direction="column"
        sx={{
          mt: "10px",
          backgroundColor: "#f5f5f5",
          borderRadius: "10px",
        }}
      >
        <Grid2
          container
          justifyContent="flex-end"
          alignItems="center"
          sx={{
            px: 2,
            py: 2,
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            gap: "12px",
          }}
        >
          <Button
            variantType="secondary"
            label="Create Invoice"
            sx={{ minWidth: 130 }}
            onClick={handleCreateInvoice}
          />

          <Button
            icon={DiscountIcon}
            label={"Apply Discount/Fee"}
            variantType="secondary"
            disabled={totalInvoiceAmount === 0}
            sx={{ minWidth: 160 }}
            onClick={() => setIsAdjustmentModalOpen(true)}
          />
        </Grid2>

        <CreateInvoiceDatagrid
          ref={datagridRef}
          loading={
            createOneOffInvoiceMutation.isLoading ||
            updateAccountFinancialMutation.isLoading
          }
          key={resetKey}
          onTotalChange={setTotalInvoiceAmount}
          totalInvoiceAmount={totalInvoiceAmount}
          onAdjustmentChange={({ discount, fee, discountPercent }) => {
            setDiscountAmount(discount);
            setFeeAmount(fee);
            setDiscountPercentTotal(discountPercent);
          }}
        />
      </Grid2>
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 1 }} />

        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", sm: "320px" },
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
            >
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                Item amount:
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {formatCurrency(totalInvoiceAmount)}
              </Typography>
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
            >
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                {discountPercentTotal > 0 && !hasBoth
                  ? `Discount to be applied (${discountPercentTotal}%):`
                  : "Discount to be applied:"}
              </Typography>

              <Typography sx={{ fontWeight: 600 }}>
                {`-${formatCurrency(totalDiscount)}`}
              </Typography>
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                Fee to be applied:
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {formatCurrency(feeAmount)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                Total Amount:
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: theme.palette.success.main,
                }}
              >
                {formatCurrency(finalAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />
        <Grid2 container spacing={2} justifyContent={{ xs: "flex-end" }}>
          <Grid2 size={{ xs: 12, md: "auto" }}>
            <Button
              variantType="secondary"
              color="primary"
              label="Cancel"
              sx={{ minWidth: 130 }}
              onClick={() => {
                const path =
                  user?.roleCode === Role.SuperAdmin
                    ? ROUTE_SADM_INVOICES
                    : ROUTE_ADM_INVOICES;

                navigate(path);
              }}
            />
          </Grid2>
        </Grid2>
      </Box>
      <CreateAccountDrawer
        open={openCreateAccountDrawer}
        onClose={() => setOpenCreateAccountDrawer(false)}
        onAccountCreated={handleAccountCreated}
        orgId={selectedOrgId}
      />
      <Modal
        open={isAdjustmentModalOpen}
        onClose={() => setIsAdjustmentModalOpen(false)}
        title="Apply Adjustments"
        content={
          <AdjustmentsForm
            id="adjustmentsForm"
            defaultValues={totalInvoiceAmount}
            handleEdit={handleAdjustmentEdit}
          />
        }
        form="adjustmentsForm"
        primaryButtonText="Apply"
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setIsAdjustmentModalOpen(false)}
      />
      <CreateRegenerateInvoiceModal
        open={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        title={"Create Invoice"}
        requestId={requestId}
        orgId={orgIdForModal}
        accountId={accountIdForModal}
        regenerate={false}
        handleRefresh={() => {
          setResetKey((prev) => prev + 1);
          setTotalInvoiceAmount(0);
          setDiscountAmount(0);
          setFeeAmount(0);
          setDiscountPercentTotal(0);
          setRequestId("");
        }}
      />
    </Box>
  );
};
